import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from 'react';
import { FunnelIcon as FunnelIconOutline } from '@heroicons/react/24/outline';
import { FunnelIcon as FunnelIconSolid } from '@heroicons/react/24/solid';
import { StatusFilter } from '../types';

interface StatusFilterDropdownProps {
    statusFilter: StatusFilter;
    onStatusFilterChange: Dispatch<SetStateAction<StatusFilter>>;
    variant?: 'map' | 'default';
    disabled?: boolean;
}

const StatusFilterDropdown: FC<StatusFilterDropdownProps> = ({
    statusFilter,
    onStatusFilterChange,
    variant = 'default',
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filterOptions: { key: keyof StatusFilter; label: string; color?: string }[] = [
        { key: 'normal', label: 'Normal', color: 'bg-success' },
        { key: 'system-fault', label: 'System fault', color: 'bg-error' },
        { key: 'no-data', label: 'No data', color: 'bg-gray-300' },
        { key: 'under-construction', label: 'Under construction', color: 'bg-[#827b7b]' },
        { key: 'manual', label: 'Manual', color: 'bg-blue-300' },
        { key: 'not-on-map', label: 'Not on the map' },
    ];

    const handleToggle = (key: keyof StatusFilter) => {
        onStatusFilterChange(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleOnly = (key: keyof StatusFilter, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const newFilter = Object.keys(statusFilter).reduce((acc, k) => {
            acc[k as keyof StatusFilter] = k === key;
            return acc;
        }, {} as StatusFilter);
        onStatusFilterChange(newFilter);
    };

    const activeFilters = Object.values(statusFilter).filter(v => v).length;
    const allFiltersActive = activeFilters === Object.keys(statusFilter).length;
    const hasInactiveFilter = !allFiltersActive;

    const handleAll = () => {
        const newFilter = Object.keys(statusFilter).reduce((acc, k) => {
            acc[k as keyof StatusFilter] = true;
            return acc;
        }, {} as StatusFilter);
        onStatusFilterChange(newFilter);
    };

    const buttonClass = variant === 'map'
        ? `rounded-lg ${hasInactiveFilter ? 'bg-white/50' : 'bg-white/25'} hover:bg-white/50 flex justify-center items-center cursor-pointer p-0 size-8`
        : `btn btn-xs btn-ghost gap-1`;

    const FunnelIcon = hasInactiveFilter ? FunnelIconSolid : FunnelIconOutline;

    return (
        <div className="relative z-[9999]" ref={dropdownRef}>
            <button
                className={buttonClass}
                onClick={() => setIsOpen(!isOpen)}
                title="Filter by status"
            >
                <FunnelIcon className="w-4 h-4" />
                {variant === 'default' && <span>Filter</span>}
            </button>
            {isOpen && (
                <div className="absolute left-0 mt-1 bg-slate-200 rounded-lg shadow-lg py-2 w-max z-[9999]">
                    <label
                        className={`flex items-center gap-2 px-3 py-2 select-none ${disabled ? 'cursor-not-allowed' : 'hover:bg-slate-300/70 cursor-pointer'}`}
                    >
                        <input
                            type="checkbox"
                            checked={allFiltersActive}
                            onChange={handleAll}
                            className="checkbox checkbox-sm border-slate-300 checked:border-pacificgreen-400 checked:bg-pacificgreen-400 checked:text-white"
                            disabled={disabled}
                        />
                        <span className="text-sm text-slate-800">All</span>
                    </label>
                    <div className="border-t border-slate-300 my-1"></div>
                    {filterOptions.map(option => (
                        <label
                            key={option.key}
                            className={`flex items-center justify-between gap-4 px-3 py-2 select-none ${disabled ? 'cursor-not-allowed' : 'hover:bg-slate-300/70 cursor-pointer'}`}
                        >
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={statusFilter[option.key]}
                                    onChange={() => handleToggle(option.key)}
                                    className="checkbox checkbox-sm border-slate-300 checked:border-pacificgreen-400 checked:bg-pacificgreen-400 checked:text-white"
                                    disabled={disabled}
                                />
                                <span className="text-sm text-slate-800">{option.label}</span>
                                <button
                                    onClick={(e) => handleOnly(option.key, e)}
                                    className={`text-xs cursor-pointer ${disabled ? 'text-gray-400 cursor-not-allowed' : 'text-pacificgreen-700 hover:text-pacificgreen-500 hover:underline'}`}
                                    disabled={disabled}
                                >
                                    only
                                </button>
                            </div>
                            {option.color && <div className={`w-3 h-3 rounded-full ${option.color}`}></div>}
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};

export default StatusFilterDropdown;
